<template>
  <b-form-select
    :name="name"
    v-model="selectedOption"
    :options="options"
    :disabled="isBusy || disabled"
    class="invision-input"
    :state="state"
    @change="valueChanged"
  >
    <template slot="first">
      <option
        :disabled="disabledFirstOption"
        :value="firstOption ? firstOption.value : null"
      >
        {{
          firstOption
            ? firstOption.label
            : '-- Por favor, selecione uma opção --'
        }}
      </option>
    </template>
  </b-form-select>
</template>

<script>
import { mapState } from 'vuex';
import GenericApi from '@/services/genericRequest';
import unidadeService from '@/services/unidade';

export default {
  name: 'GenericSelect',
  props: {
    name: {
      type: String,
      required: false,
    },
    // Default value of the input, also used for edition
    value: {
      type: [Number, String],
      default: null,
    },
    // Indicates if the field is disabled
    disabled: {
      type: Boolean,
      default: false,
    },
    state: {
      type: Boolean,
      required: false,
      default: null,
    },
    disabledFirstOption: {
      type: Boolean,
      default: false,
    },

    // Method used to retrieve the available oprions
    apiFunction: {
      type: String,
      required: false,
      default() {
        return 'getWithoutPagination';
      },
    },
    api: {
      type: Object,
      required: false,
      default() {
        return GenericApi;
      },
    },
    route: {
      type: String,
      required: true,
    },

    // Filters used on the api method
    filters: {
      type: Object,
      default() {
        return {};
      },
    },
    // Filters used on the api method
    customFilters: {
      type: Object,
      default() {
        return {};
      },
    },
    // Name of the attribute that contains the displayed label
    labelKey: {
      type: String,
      required: true,
    },
    firstOption: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      isBusy: false,
      options: [],
      selectedOption: this.value,
      apiResponse: [],
    };
  },

  computed: {
    ...mapState({
      genericData: (state) => state.genericData,
    }),
  },

  mounted() {
    this.fillOptions();
  },

  watch: {
    customFilters(newV, oldV) {
      if (JSON.stringify(newV) !== JSON.stringify(oldV)) {
        if (this.selectedOption) {
          this.selectedOption = null;
          this.valueChanged();
        }
        this.fillOptions();
      }
    },

    value() {
      this.selectedOption = this.value;
    },
  },

  methods: {
    valueChanged() {
      if (!this.disabled) {
        this.$emit('input', this.selectedOption);
      }
    },

    async fillOptions() {
      this.options = [];
      this.isBusy = true;
      const { route, customFilters } = this;

      const result = await this.fetchData(route, customFilters);

      if (!result || !result.length) {
        this.isBusy = false;
        return;
      }

      this.apiResponse = result;

      const idKey = this.getIdKeyFromObject(result[0]);
      this.options = result.map((row) => ({
        value: row[idKey],
        text: row[this.labelKey],
      }));

      this.selectedOption = this.value;
      this.$emit('filled', this.selectedOption);
      if (this.options.length === 1 && this.disabledFirstOption) {
        this.selectedOption = this.options[0].value;
        this.valueChanged();
      }
      this.isBusy = false;
    },

    getIdKeyFromObject(object) {
      return Object.keys(object).find((key) => key.slice(0, 2) === 'id');
    },

    async fetchData(route, customFilters) {
      const specialRoutes = {
        tipoManutencao: this.fetchTipoManutencao,
        setor: this.fetchSetor,
        equipamento: this.fetchEquipamento,
        subSetor: this.fetchSubSetor,
        tipoProcesso: this.fetchTipoProcesso,
      };

      if (specialRoutes[route]) {
        return specialRoutes[route](customFilters);
      }

      return this.api[this.apiFunction]({ customFilters }, route);
    },

    async fetchTipoManutencao(customFilters) {
      const tiposManutencao = await this.api[this.apiFunction](
        { customFilters },
        'tipoManutencao',
      );

      if (customFilters.id_tipo_manutencao) {
        return tiposManutencao.filter((tm) => customFilters.id_tipo_manutencao.includes(tm.id_tipo_manutencao));
      }

      return tiposManutencao;
    },

    async fetchSetor(customFilters) {
      let setores = await this.api[this.apiFunction](
        { customFilters },
        'setor',
      );

      if (customFilters.id_setor) {
        const ids = Array.isArray(customFilters.id_setor)
          ? customFilters.id_setor
          : [customFilters.id_setor];
        setores = setores.filter((s) => ids.includes(s.id_setor));
      }

      return setores.sort((a, b) => {
        if (a.nome.toUpperCase() < b.nome.toUpperCase()) return -1;
        return 1;
      });
    },

    async fetchEquipamento(customFilters) {
      if (!customFilters.id_tipo_esterilizacao || !customFilters.id_unidade) {
        return this.api[this.apiFunction]({ customFilters }, 'equipamento');
      }

      const tiposEsterilizacao = await this.api[this.apiFunction](
        {},
        'tipoEsterilizacao',
      );
      const tipoEsterilizacao = tiposEsterilizacao.find(
        (te) => te.id_tipo_esterilizacao === customFilters.id_tipo_esterilizacao,
      );

      if (!tipoEsterilizacao || !tipoEsterilizacao.equipamentos) {
        return [];
      }

      return tipoEsterilizacao.equipamentos.filter(
        (equip) => equip.id_unidade === customFilters.id_unidade,
      );
    },

    async fetchSubSetor(customFilters) {
      if (!customFilters.id_unidade) {
        if (this.genericData.allSubSetor) return [...this.genericData.allSubSetor];
        if (this.genericData.subSetor) return [...this.genericData.subSetor];
        return [];
      }

      const unidades = await unidadeService.getUnidades();
      const unidade = unidades.find(
        (elem) => elem.id_unidade === customFilters.id_unidade,
      );

      if (!unidade || !unidade.tipoProcesso) {
        return [];
      }

      const subSetores = this.extractSubSetoresFromUnidade(unidade);
      subSetores.sort((a, b) => a.id_sub_setor - b.id_sub_setor);

      return [...subSetores];
    },

    extractSubSetoresFromUnidade(unidade) {
      return unidade.tipoProcesso.reduce((acum, curr) => {
        let subSets = curr.processo.reduce((ac, proc) => {
          if (
            ac.find(
              (item) => item.id_sub_setor === proc.fluxoSubSetor.id_sub_setor,
            )
          ) {
            return ac;
          }
          return [...ac, proc.fluxoSubSetor.subSetor];
        }, []);

        if (
          curr.tipoMaterialGenericoTipoProcesso
          && curr.tipoMaterialGenericoTipoProcesso[0]
          && curr.tipoMaterialGenericoTipoProcesso[0].id_sub_setor
        ) {
          subSets = curr.tipoMaterialGenericoTipoProcesso.reduce(
            (acc, tmgTp) => {
              if (acc.find((item) => item.id_sub_setor === tmgTp.id_sub_setor)) {
                return acc;
              }
              return [
                ...acc,
                { id_sub_setor: tmgTp.id_sub_setor, ...tmgTp.subSetor },
              ];
            },
            subSets,
          );
        }

        const newAcum = [...acum];
        const { UnidadeTipoProcesso, processo, ...tipoProcessoData } = curr;

        subSets.forEach((subSet) => {
          const prevSubSet = newAcum.find(
            (item) => item.id_sub_setor === subSet.id_sub_setor,
          );
          if (!prevSubSet) {
            newAcum.push({
              ...subSet,
              tipoProcesso: [tipoProcessoData],
            });
          } else {
            prevSubSet.tipoProcesso.push(tipoProcessoData);
          }
        });

        return newAcum;
      }, []);
    },

    async fetchTipoProcesso(customFilters) {
      const tiposProcessos = await this.api[this.apiFunction](
        { customFilters },
        'tipoProcesso',
      );

      if (
        customFilters.id_tipo_processo
        && customFilters.id_tipo_processo.length > 0
      ) {
        return tiposProcessos.filter((tm) => customFilters.id_tipo_processo.includes(tm.id_tipo_processo));
      }

      return tiposProcessos;
    },

    getChaveById(id, varName) {
      const result = this.apiResponse.filter((elem) => elem[varName] === id);
      return result[0].chave;
    },
  },
};
</script>
